// components/common/Tooltip.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
}

// Create a single portal container outside the component
let portalContainer: HTMLDivElement | null = null;

const getPortalContainer = () => {
  if (!portalContainer && typeof document !== 'undefined') {
    const existing = document.getElementById('tooltip-portal');
    if (existing) {
      portalContainer = existing as HTMLDivElement;
    } else {
      portalContainer = document.createElement('div');
      portalContainer.id = 'tooltip-portal';
      portalContainer.style.position = 'fixed';
      portalContainer.style.top = '0';
      portalContainer.style.left = '0';
      portalContainer.style.zIndex = '9999';
      portalContainer.style.pointerEvents = 'none';
      document.body.appendChild(portalContainer);
    }
  }
  return portalContainer;
};

export const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  position = 'top',
  delay = 300,
  className = ""
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsVisible(false);
    };
  }, []);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = rect.top + scrollY - 8;
        left = rect.left + rect.width / 2 + scrollX;
        break;
      case 'bottom':
        top = rect.bottom + scrollY + 8;
        left = rect.left + rect.width / 2 + scrollX;
        break;
      case 'left':
        top = rect.top + rect.height / 2 + scrollY;
        left = rect.left + scrollX - 8;
        break;
      case 'right':
        top = rect.top + rect.height / 2 + scrollY;
        left = rect.right + scrollX + 8;
        break;
    }

    setTooltipPosition({ top, left });
  }, [position]);

  const handleMouseEnter = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        calculatePosition();
        setIsVisible(true);
      }
    }, delay);
  }, [delay, calculatePosition]);

  const handleMouseLeave = useCallback(() => {
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Hide tooltip
    if (isMountedRef.current) {
      setIsVisible(false);
    }
  }, []);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isVisible) return;

    const handleUpdate = () => {
      if (isMountedRef.current && isVisible) {
        calculatePosition();
      }
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isVisible, calculatePosition]);

  const getTooltipContent = () => {
    switch (content) {
      case 'ET':
        return 'ET (Estimated time)';
      case 'TT':
        return 'TT (Time taken)';
      default:
        return content.toLowerCase();
    }
  };

  const TooltipContent = () => (
    <div
      className="fixed px-2 py-[2px] text-xs font-medium text-white bg-gray-900 rounded-lg shadow-xl whitespace-nowrap"
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        zIndex: 9999,
        transform: position === 'top' || position === 'bottom' 
          ? 'translate(-50%, -100%)' 
          : position === 'left' 
          ? 'translate(-100%, -50%)' 
          : 'translate(0, -50%)',
      }}
    >
      {getTooltipContent()}
      {/* Arrow pointing down (for top position) */}
      {position === 'top' && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1"
          style={{
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid rgb(17, 24, 39)',
          }}
        />
      )}
    </div>
  );

  const portal = getPortalContainer();

  return (
    <>
      <div 
        ref={triggerRef}
        className={clsx("relative inline-flex w-full h-full items-center px-1", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {isVisible && content && portal && ReactDOM.createPortal(
        <TooltipContent key={`tooltip-${content}`} />,
        portal
      )}
    </>
  );
};

export default Tooltip;